/**
 * exportReport — Markdown + PDF Export für Reports
 *
 * Konvertiert den TipTap-Editor-Inhalt in:
 *   1. Markdown (.md)  — sauber formatiert
 *   2. PDF (.pdf)      — über HTML → print-optimiertes Stylesheet
 */

import { Editor } from '@tiptap/react'

// ─── Markdown-Export ─────────────────────────────────────────────────────────

/**
 * Wandelt TipTap-JSON rekursiv in Markdown um.
 */
function tiptapJsonToMarkdown(doc: any): string {
  if (!doc || !doc.content) return ''
  return doc.content.map((node: any) => nodeToMd(node)).join('\n\n')
}

function nodeToMd(node: any): string {
  switch (node.type) {
    case 'heading': {
      const level = node.attrs?.level || 1
      const prefix = '#'.repeat(level)
      return `${prefix} ${inlineToMd(node.content)}`
    }
    case 'paragraph':
      return inlineToMd(node.content)
    case 'bulletList':
      return (node.content || []).map((li: any) => listItemToMd(li, '- ')).join('\n')
    case 'orderedList':
      return (node.content || []).map((li: any, i: number) => listItemToMd(li, `${i + 1}. `)).join('\n')
    case 'listItem':
      return (node.content || []).map((n: any) => nodeToMd(n)).join('\n')
    case 'blockquote':
      return (node.content || []).map((n: any) => `> ${nodeToMd(n)}`).join('\n> \n')
    case 'codeBlock': {
      const lang = node.attrs?.language || ''
      const code = inlineToMd(node.content)
      return '```' + lang + '\n' + code + '\n```'
    }
    case 'horizontalRule':
      return '---'
    case 'image': {
      const alt = node.attrs?.alt || ''
      const src = node.attrs?.src || ''
      return `![${alt}](${src})`
    }
    case 'youtube': {
      const src = node.attrs?.src || ''
      return `[YouTube Video](${src})`
    }
    // Custom nodes → als lesbare Platzhalter
    case 'annotationLink': {
      const label = node.attrs?.label || 'Annotation'
      return `📌 *${label}*`
    }
    case 'viewSnapshot': {
      const label = node.attrs?.label || 'Ansicht'
      return `📷 *${label}*`
    }
    case 'paperlessDocLink': {
      const title = node.attrs?.title || 'Dokument'
      return `📄 *${title}*`
    }
    case 'auditLink': {
      const label = node.attrs?.label || 'Audit'
      return `🔍 *${label}*`
    }
    default:
      // Unbekannte Nodes: Kinder rendern
      if (node.content) return node.content.map((n: any) => nodeToMd(n)).join('')
      return ''
  }
}

function listItemToMd(li: any, prefix: string): string {
  const lines = (li.content || []).map((n: any) => nodeToMd(n))
  return lines.map((l: string, i: number) => i === 0 ? `${prefix}${l}` : `  ${l}`).join('\n')
}

function inlineToMd(content: any[] | undefined): string {
  if (!content) return ''
  return content.map((node: any) => {
    if (node.type === 'text') {
      let t = node.text || ''
      const marks = node.marks || []
      for (const mark of marks) {
        switch (mark.type) {
          case 'bold': t = `**${t}**`; break
          case 'italic': t = `*${t}*`; break
          case 'code': t = `\`${t}\``; break
          case 'strike': t = `~~${t}~~`; break
          case 'link': t = `[${t}](${mark.attrs?.href || ''})`; break
        }
      }
      return t
    }
    if (node.type === 'hardBreak') return '\n'
    return nodeToMd(node)
  }).join('')
}

// ─── PDF-Export ──────────────────────────────────────────────────────────────

/**
 * Erstellt ein druckoptimiertes HTML-Dokument aus dem Editor-HTML
 * und löst den Browser-Druckdialog aus (Save as PDF).
 */
function buildPrintHtml(title: string, htmlContent: string): string {
  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>${escHtml(title)}</title>
<style>
  @page {
    size: A4;
    margin: 25mm 20mm;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'PP Mori', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 11pt;
    line-height: 1.6;
    color: #1a1a1a;
    max-width: 100%;
  }
  /* Title */
  .report-title {
    font-size: 20pt;
    font-weight: 700;
    margin-bottom: 0.25em;
    color: #111;
  }
  .report-meta {
    font-size: 9pt;
    color: #888;
    margin-bottom: 2em;
    border-bottom: 1px solid #e5e5e5;
    padding-bottom: 1em;
  }
  /* Headings */
  h1 { font-size: 16pt; font-weight: 700; margin: 1.2em 0 0.4em; }
  h2 { font-size: 13pt; font-weight: 600; margin: 1em 0 0.3em; }
  h3 { font-size: 11pt; font-weight: 600; margin: 0.8em 0 0.2em; }
  /* Text */
  p { margin: 0.5em 0; }
  strong { font-weight: 600; }
  em { font-style: italic; }
  a { color: #2563eb; text-decoration: none; }
  /* Lists */
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  li { margin: 0.15em 0; }
  /* Blockquote */
  blockquote {
    border-left: 3px solid #d1d5db;
    padding-left: 1em;
    margin: 0.5em 0;
    color: #4b5563;
  }
  /* Code */
  code {
    font-family: 'Sohne Mono', 'SF Mono', 'Fira Code', monospace;
    font-size: 0.9em;
    background: #f3f4f6;
    padding: 0.1em 0.3em;
    border-radius: 3px;
  }
  pre {
    background: #f3f4f6;
    padding: 0.8em 1em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;
    font-size: 0.85em;
    line-height: 1.5;
  }
  pre code { background: none; padding: 0; }
  /* Images */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 0.5em 0;
  }
  /* Horizontal rule */
  hr {
    border: none;
    border-top: 1px solid #e5e5e5;
    margin: 1em 0;
  }
  /* Embedded cards (annotations, audits, snapshots, docs) */
  .embed-card {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    padding: 0.2em 0.6em;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    font-size: 0.9em;
    color: #374151;
    page-break-inside: avoid;
  }
  /* Table */
  table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
  th, td { border: 1px solid #d1d5db; padding: 0.4em 0.6em; text-align: left; font-size: 0.9em; }
  th { background: #f3f4f6; font-weight: 600; }
  /* Print */
  @media print {
    body { font-size: 10pt; }
    a { color: #1a1a1a; }
    .no-print { display: none !important; }
  }
</style>
</head>
<body>
<div class="report-title">${escHtml(title)}</div>
<div class="report-meta">Exportiert am ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
${htmlContent}
</body>
</html>`
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Exportiert den Report als Markdown-Datei (Download).
 */
export function exportAsMarkdown(editor: Editor, title: string) {
  const json = editor.getJSON()
  const md = `# ${title}\n\n${tiptapJsonToMarkdown(json)}`
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
  downloadBlob(blob, sanitizeFilename(title) + '.md')
}

/**
 * Exportiert den Report als PDF (öffnet Print-Dialog zum Speichern).
 */
export function exportAsPdf(editor: Editor, title: string) {
  const html = editor.getHTML()
  const printHtml = buildPrintHtml(title, html)

  const printWin = window.open('', '_blank')
  if (!printWin) {
    alert('Popup-Blocker verhindert den PDF-Export. Bitte Popups erlauben.')
    return
  }
  printWin.document.write(printHtml)
  printWin.document.close()
  // Kurz warten, damit Bilder laden
  setTimeout(() => {
    printWin.focus()
    printWin.print()
  }, 400)
}

/**
 * Zeigt ein Export-Menü (MD oder PDF).
 */
export type ExportFormat = 'md' | 'pdf'

export function exportReport(editor: Editor, title: string, format: ExportFormat) {
  if (format === 'md') exportAsMarkdown(editor, title)
  else exportAsPdf(editor, title)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 100)
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, '_')
    .substring(0, 80)
    || 'report'
}
